import { useState } from 'react'
import classNames from 'classnames'

import imageToBase64 from 'helpers/imageToBase64'
import SelectField from 'components/SelectField'
import ApplyFormLabel from '../ApplyFormLabel'
import ApplyStepIcon from 'components/ApplyStepIcon'
import ApplyStepButtons from 'components/ApplyStepButtons'

import s from './ApplyFormMeasurements.module.css'
import * as Sentry from '@sentry/nextjs'

const fields = {
  male: [
    'height',
    'waist',
    'hips',
    'inseam',
    'neck',
    'sleeve',
    { attr: 'suitSize', label: 'Suit size' },
    { attr: 'suitCut', label: 'Suit cut' },
  ],
  female: [
    'height',
    'bust',
    'waist',
    'hips',
    'cup'
  ],
  'non-binary': [
    'height'
  ],
  other: [
    'height'
  ]
}

const showUnitLabel = (attr) => {
  if (
    attr === 'shoeSize' ||
    attr === 'cup' ||
    attr === 'suitCut' ||
    attr === 'suitSize'
  )
    return false
  return true
}

const toFeet = (n) => {
  const realFeet = (n * 0.3937) / 12
  const feet = Math.floor(realFeet)
  const inches = Math.round((realFeet - feet) * 12)
  return `${feet}" ${inches}'`
}

//TODO: setup proptypes
function ApplyFormMeasurements({ onSubmit, goBack, initialValues }) {
  const [errors, setErrors] = useState([])
  const [units, setUnits] = useState('metric')
  const [shoeSizeUnits, setShoeSizeUnits] = useState('eu')
  const [fieldValues, setFieldValues] = useState({
    gender: initialValues.gender || null,
    height: initialValues.height || '',
    bust: initialValues.bust || '',
    waist: initialValues.waist || '',
    hips: initialValues.hips || '',
    cup: initialValues.cup || '',
    inseam: initialValues.inseam || '',
    neck: initialValues.neck || '',
    sleeve: initialValues.sleeve || '',
    suitSize: initialValues.suitSize || '',
    suitCut: initialValues.suitCut || '',
    shoeSize: initialValues.shoeSize || null,
    hairColour: initialValues.hairColour || null,
    eyeColour: initialValues.eyeColour || null,
  })

  const handleAttrChange = (attr, val) => {
    const fields = { ...fieldValues }
    fields[attr] = val
    if (attr === 'gender') setErrors([])
    setFieldValues(fields)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const requiredFields = ['height']
    const newErrors = requiredFields.filter((f) => !fieldValues[f])
    if (!fieldValues.eyeColour) newErrors.push('eyeColour')
    if (!fieldValues.hairColour) newErrors.push('hairColour')
    if (newErrors.length) {
      setErrors(newErrors)
    } else {

      const submitValues = {...fieldValues}

      Sentry.addBreadcrumb({
        category: 'form',
        message: 'Measurements Submitted',
        level: 'debug',
        data: submitValues,
      })

      onSubmit(submitValues)
    }
  }

  const handleFileSelect = (attr, acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const fields = { ...fieldValues }
      fields[attr] = {
        name: acceptedFiles[0].file.name,
        preview: acceptedFiles[0].preview,
        data: null,
        loading: true,
      }
      setFieldValues(fields)
      imageToBase64(acceptedFiles[0].file, {
        maxHeight: 1000,
        maxWidth: 1000,
      }).then((resp) => {
        setFieldValues((curState) => {
          const curFields = { ...curState }
          curFields[attr].data = resp.base64
          curFields[attr].loading = false
          return { ...curFields }
        })
      })
    }
  }

  const getMeasurementOptions = (attr, gender) => {
    const heightSteps = gender !== 'female' ? 18 : 14
    const options = (() => {
      switch (attr) {
        case 'height':
          return Array.from(Array(heightSteps), (_, x) => {
            const value = 164 + x * 2
            return {
              value: `${value} CM / ${toFeet(value)}'`,
              label: units === 'metric' ? `${value} CM` : `${toFeet(value)}'`,
            }
          })
        case 'bust':
          return Array.from(Array(51), (_, x) => {
            const value = 70 + x
            return {
              value: `${value} CM / ${Math.round(value * 0.3937)}'`,
              label:
                units === 'metric'
                  ? `${value} CM`
                  : `${Math.round(value * 0.3937)}'`,
            }
          })
        case 'waist':
          return Array.from(Array(44), (_, x) => {
            const value = 57 + x
            return {
              value: `${value} CM / ${Math.round(value * 0.3937)}'`,
              label:
                units === 'metric'
                  ? `${value} CM`
                  : `${Math.round(value * 0.3937)}'`,
            }
          })
        case 'hips':
          return Array.from(Array(72), (_, x) => {
            const value = 69 + x
            return {
              value: `${value} CM / ${Math.round(value * 0.3937)}'`,
              label:
                units === 'metric'
                  ? `${value} CM`
                  : `${Math.round(value * 0.3937)}'`,
            }
          })
        case 'sleeve':
        case 'neck':
        case 'inseam':
          return Array.from(Array(180), (_, x) => {
            const value = 1 + x
            return {
              value: `${value} CM / ${Math.round(value * 0.3937)}'`,
              label:
                units === 'metric'
                  ? `${value} CM`
                  : `${Math.round(value * 0.3937)}'`,
            }
          })
        case 'suitSize':
          return Array.from(Array(9), (_, x) => {
            const value = 42 + x * 2
            return {
              value: `${value} / ${32 + x * 2}'`,
              label: units === 'metric' ? value : 32 + x * 2,
            }
          })
        case 'suitCut':
          return ['X Short', 'Short', 'Regular', 'Long', 'X Long', 'XX Long']
        case 'cup':
          return ['AA', 'A', 'B', 'B/C', 'C', 'D', 'DD', 'E', 'F']
        case 'hairColour':
          return [
            'Blonde',
            'Dark Blonde',
            'Brown',
            'Dark Brown',
            'Black',
            'Red',
            'Red Blonde',
            'Light Brown',
            'Red Brown',
            'Grey',
            'Grey/Black',
          ]
        case 'eyeColour':
          return [
            'Blue',
            'Blue/Green',
            'Green',
            'Green/Brown',
            'Brown',
            'Dark/Brown',
            'Blue/Grey',
            'Hazel',
            'Green/Grey',
            'Grey',
          ]
        case 'shoeSize': {
          const sizes =
            fieldValues.gender === 'male'
              ? [
                  [40, 5, 6],
                  [40, 5.5, 6.5],
                  [41, 6, 7],
                  [41, 6.5, 7.5],
                  [42, 7, 8],
                  [42, 7.5, 8.5],
                  [43, 8, 9],
                  [43, 8.5, 9.5],
                  [44, 9, 10],
                  [44, 9.5, 10.5],
                  [45, 10, 11],
                  [45, 10.5, 11.5],
                  [46, 11, 12],
                  [47, 12, 13],
                  [48, 13, 14],
                  [49, 14, 15],
                  [50, 15, 16],
                ]
              : [
                  [35, 3, 5.5],
                  [36, 3.5, 6],
                  [37, 4, 7],
                  [38, 5, 7.5],
                  [39, 6, 8],
                  [40, 7.5, 9],
                  [41, 8, 10],
                  [42, 8.5, 11],
                  [43, 9, 11.5],
                ]
          return sizes.map((size) => {
            let label = size[0]
            if (shoeSizeUnits !== 'eu')
              label = shoeSizeUnits === 'uk' ? size[1] : size[2]
            return {
              value: `EU ${size[0]} / UK ${size[1]} / US ${size[2]}`,
              label,
            }
          })
        }
        default:
          return Array.from(Array(22), (_, x) => {
            const value = 30 + x
            return {
              value,
              label: value,
            }
          })
      }
    })()
    return options
  }

  const toggleUnits = () => {
    setUnits(units === 'metric' ? 'imperial' : 'metric')
  }

  const handleChangeShoeSizeUnits = (newVal) => {
    setShoeSizeUnits(newVal)
  }

  const activeFields = fields[fieldValues.gender]

  return (
    <div className={s.container}>
      <form onSubmit={handleSubmit}>
        <h1 className={s.title}>Gender & Measurements</h1>
        <p className={s.text}>Please provide accurate measurements.</p>
        <div className={s.field}>
          <ApplyFormLabel errors={errors} attr="gender" label="Gender" />
          <SelectField
            onChange={handleAttrChange}
            name="gender"
            options={[
              {
                label: 'Female',
                value: 'female',
              },
              {
                label: 'Male',
                value: 'male',
              },
              {
                label: 'Non-binary',
                value: 'non-binary',
              },
              {
                label: 'Other',
                value: 'other',
              },
            ]}
            label="Please select your gender"
            value={fieldValues.gender}
            optionsClassName={s.selectGenderOptions}
            optionClassName={s.selectOption}
            inputClassName={s.selectInput}
          />
        </div>
        {fieldValues.gender && (
          <div className={s.columns}>
            <div className={s.column}>
              {activeFields
                .slice(0, fieldValues.gender === 'male' ? 6 : 4)
                .map((f) => {
                  const selectOptions = getMeasurementOptions(f.attr || f, fieldValues.gender)
                  const value = fieldValues[f.attr || f]
                  return (
                    <div className={s.field} key={f.attr || f}>
                      <ApplyFormLabel
                        errors={errors}
                        attr={f}
                        label={f.label || f}
                      />
                      <div className={s.inputWrapper}>
                        <SelectField
                          onChange={handleAttrChange}
                          name={f.attr || f}
                          id={f.attr || f}
                          options={selectOptions.filter(
                            (elem, pos, arr) =>
                              arr.findIndex((e) => e.label === elem.label) ===
                              pos
                          )}
                          labelFormatter={(l) => {
                            const opt = selectOptions.find(
                              (o) => o.value === value
                            )
                            if (opt) return opt.label.replace(' CM', '')
                            return typeof l === 'string'
                              ? l.replace(' CM', '')
                              : l
                          }}
                          value={value}
                          optionsClassName={s.selectOptions}
                          inputClassName={s.selectInput}
                        />
                        {showUnitLabel(f.attr || f) && (
                          <button
                            type="button"
                            className={s.fieldMetric}
                            onClick={toggleUnits}
                          >
                            <span
                              className={
                                units === 'metric' ? s.fieldMetricActive : null
                              }
                            >
                              CM
                            </span>{' '}
                            /{' '}
                            <span
                              className={
                                units === 'imperial'
                                  ? s.fieldMetricActive
                                  : null
                              }
                            >
                              Inch
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
            </div>
            <div className={s.column}>
              {activeFields
                .slice(fieldValues.gender === 'male' ? 6 : 4)
                .map((f) => (
                  <div className={s.field} key={f.attr || f}>
                    <ApplyFormLabel
                      errors={errors}
                      attr={f}
                      label={f.label || f}
                    />
                    <div className={s.inputWrapper}>
                      <SelectField
                        onChange={handleAttrChange}
                        name={f.attr || f}
                        id={f.attr || f}
                        options={getMeasurementOptions(f.attr || f, fieldValues.gender)}
                        labelFormatter={(l) =>
                          typeof l === 'string' ? l.replace(' CM', '') : l
                        }
                        value={fieldValues[f.attr || f]}
                        optionsClassName={s.selectOptions}
                        inputClassName={s.selectInput}
                      />
                      {showUnitLabel(f.attr || f) && (
                        <button
                          type="button"
                          className={s.fieldMetric}
                          onClick={toggleUnits}
                        >
                          <span
                            className={
                              units === 'metric' ? s.fieldMetricActive : null
                            }
                          >
                            CM
                          </span>{' '}
                          /{' '}
                          <span
                            className={
                              units === 'imperial' ? s.fieldMetricActive : null
                            }
                          >
                            Inch
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              <div className={s.field}>
                <ApplyFormLabel
                  errors={errors}
                  attr={{ attr: 'shoeSize', label: 'Shoe size' }}
                  label="Shoe size (EU)"
                />
                <div className={s.inputWrapper}>
                  <SelectField
                    onChange={handleAttrChange}
                    name="shoeSize"
                    options={getMeasurementOptions('shoeSize', fieldValues.gender).filter(
                      (elem, pos, arr) =>
                        arr.findIndex((e) => e.label === elem.label) === pos
                    )}
                    label="Please provide your shoe size"
                    value={fieldValues.shoeSize}
                    optionsClassName={s.selectOptions}
                    optionClassName={s.selectOption}
                    inputClassName={s.selectInput}
                  />
                  <div className={s.fieldMetric}>
                    <button
                      type="button"
                      className={classNames(s.fieldMetricButton, {
                        [s.fieldMetricActive]: shoeSizeUnits === 'eu',
                      })}
                      onClick={() => handleChangeShoeSizeUnits('eu')}
                    >
                      EU
                    </button>{' '}
                    /{' '}
                    <button
                      type="button"
                      className={classNames(s.fieldMetricButton, {
                        [s.fieldMetricActive]: shoeSizeUnits === 'us',
                      })}
                      onClick={() => handleChangeShoeSizeUnits('us')}
                    >
                      US
                    </button>{' '}
                    /{' '}
                    <button
                      type="button"
                      className={classNames(s.fieldMetricButton, {
                        [s.fieldMetricActive]: shoeSizeUnits === 'uk',
                      })}
                      onClick={() => handleChangeShoeSizeUnits('uk')}
                    >
                      UK
                    </button>
                  </div>
                </div>
              </div>
              <div className={s.field}>
                <ApplyFormLabel
                  errors={errors}
                  attr={{ attr: 'hairColour', label: 'Hair colour' }}
                  label="Hair colour"
                />
                <SelectField
                  onChange={handleAttrChange}
                  name="hairColour"
                  options={getMeasurementOptions('hairColour', fieldValues.gender)}
                  label="What's your hair colour?"
                  value={fieldValues.hairColour}
                  optionsClassName={s.selectOptions}
                  optionClassName={s.selectOption}
                  inputClassName={s.selectInput}
                />
              </div>
              <div className={s.field}>
                <ApplyFormLabel
                  errors={errors}
                  attr={{ attr: 'eyeColour', label: 'Eye colour' }}
                  label="Eye colour"
                />
                <SelectField
                  onChange={handleAttrChange}
                  name="eyeColour"
                  options={getMeasurementOptions('eyeColour', fieldValues.gender)}
                  label="What's your eye colour?"
                  value={fieldValues.eyeColour}
                  optionsClassName={s.selectOptions}
                  optionClassName={s.selectOption}
                  inputClassName={s.selectInput}
                />
              </div>
            </div>
          </div>
        )}
        <ApplyStepButtons back={goBack} />
      </form>
      <ApplyStepIcon step="3" />
    </div>
  )
}

export default ApplyFormMeasurements
